import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Table,
    Form, Badge, Pagination, OverlayTrigger, Tooltip,
    ProgressBar, Modal
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency, formatDate, formatNumber } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function EncoursClients() {
    const navigate = useNavigate();
    const [resume, setResume] = useState(null);
    const [encours, setEncours] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [filterRetard, setFilterRetard] = useState('tous');
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [clientDetail, setClientDetail] = useState(null);
    const [facturesClient, setFacturesClient] = useState([]);
    const [loadingDetail, setLoadingDetail] = useState(false);

    useEffect(() => {
        fetchDonnees();
    }, [filterRetard]);

    const fetchDonnees = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            
            const resumeResponse = await fetch(
                `${API_BASE_URL}/finances.php?action=resume_encours`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            let url = `${API_BASE_URL}/finances.php?action=encours_clients`;
            const params = new URLSearchParams();
            if (filterText) params.append('recherche', filterText);
            if (filterRetard !== 'tous') params.append('retard', filterRetard);
            if (params.toString()) url += '&' + params.toString();
            
            const encoursResponse = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!resumeResponse.ok || !encoursResponse.ok) {
                throw new Error("Erreur chargement des données");
            }
            
            const resumeResult = await resumeResponse.json();
            const encoursResult = await encoursResponse.json();
            
            setResume(resumeResult.data);
            setEncours(encoursResult.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchDetailClient = async (idClient) => {
        setLoadingDetail(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/finances.php?action=detail_encours&id_client=${idClient}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setFacturesClient(result.data || []);
            setShowDetailModal(true);
        } catch (err) {
            alert("Erreur: " + err.message);
        } finally {
            setLoadingDetail(false);
        }
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = encours.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(encours.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const getRetourBadge = (jours) => {
        if (jours < 0) {
            return <span className="status-badge" style={{ background: '#10b98115', color: '#10b981' }}>À venir</span>;
        } else if (jours <= 30) {
            return <span className="status-badge" style={{ background: '#f59e0b15', color: '#f59e0b' }}>{jours}j</span>;
        } else if (jours <= 60) {
            return <span className="status-badge" style={{ background: '#ef444415', color: '#ef4444' }}>{jours}j</span>;
        } else {
            return <span className="status-badge" style={{ background: '#ef444415', color: '#ef4444' }}>+60j</span>;
        }
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des encours clients...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchDonnees}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-bank-card-line fs-20 text-primary"></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Encours clients</h4>
                                    <p className="text-muted small mb-0">Suivi des créances clients</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/stats/dashboard')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards modernes */}
                    {resume && (
                        <Row className="g-3 mb-4">
                            <Col sm={6} xl={4}>
                                <div className="kpi-card kpi-danger">
                                    <div className="kpi-icon">
                                        <i className="ri-money-euro-circle-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Total créances</span>
                                        <h3 className="kpi-value">{formatCurrency(resume.total?.total_creances || 0)}</h3>
                                        <span className="kpi-unit">{resume.total?.nb_factures_impayees || 0} factures</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6} xl={4}>
                                <div className="kpi-card kpi-warning">
                                    <div className="kpi-icon">
                                        <i className="ri-group-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Clients débiteurs</span>
                                        <h3 className="kpi-value">{resume.total?.nb_clients_debiteurs || 0}</h3>
                                        <span className="kpi-unit">avec encours</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6} xl={4}>
                                <div className="kpi-card kpi-info">
                                    <div className="kpi-icon">
                                        <i className="ri-calculator-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Encours moyen</span>
                                        <h3 className="kpi-value" style={{ fontSize: '1rem' }}>
                                            {formatCurrency((resume.total?.total_creances || 0) / (resume.total?.nb_clients_debiteurs || 1))}
                                        </h3>
                                        <span className="kpi-unit">par client</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Répartition par ancienneté */}
                    {resume && resume.anciennete && (
                        <Card className="border-0 shadow-sm rounded-3 mb-4">
                            <Card.Header className="bg-transparent border-0 pt-4 pb-3">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="section-icon-small">
                                        <i className="ri-timer-line text-primary"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Répartition par ancienneté</h6>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-4 pt-0">
                                <Row className="g-3">
                                    <Col md={3}>
                                        <div className="stat-card-mini">
                                            <small className="text-muted">À venir</small>
                                            <h5 className="mb-0 text-success">{formatCurrency(resume.anciennete.a_venir || 0)}</h5>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="stat-card-mini">
                                            <small className="text-muted">1-30 jours</small>
                                            <h5 className="mb-0 text-warning">{formatCurrency(resume.anciennete.retard_30j || 0)}</h5>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="stat-card-mini">
                                            <small className="text-muted">31-60 jours</small>
                                            <h5 className="mb-0 text-danger">{formatCurrency(resume.anciennete.retard_60j || 0)}</h5>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="stat-card-mini">
                                            <small className="text-muted">+60 jours</small>
                                            <h5 className="mb-0 text-danger">{formatCurrency(resume.anciennete.retard_plus_60j || 0)}</h5>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Filtres modernes */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-end g-3">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className="ri-search-line me-2"></i>
                                            Recherche
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Client, code..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="rounded-pill"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className="ri-filter-line me-2"></i>
                                            Filtre retard
                                        </Form.Label>
                                        <Form.Select 
                                            value={filterRetard} 
                                            onChange={(e) => setFilterRetard(e.target.value)}
                                            className="rounded-pill"
                                        >
                                            <option value="tous">Tous</option>
                                            <option value="a_venir">À venir</option>
                                            <option value="retard_30">1-30 jours</option>
                                            <option value="retard_60">31-60 jours</option>
                                            <option value="retard_plus">+60 jours</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className="ri-list-settings-line me-2"></i>
                                            Affichage
                                        </Form.Label>
                                        <Form.Select 
                                            value={itemsPerPage} 
                                            onChange={(e) => {
                                                setItemsPerPage(parseInt(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            className="rounded-pill"
                                        >
                                            <option value="5">5 par page</option>
                                            <option value="10">10 par page</option>
                                            <option value="20">20 par page</option>
                                            <option value="50">50 par page</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Button 
                                        variant="primary" 
                                        onClick={fetchDonnees}
                                        className="w-100 rounded-pill py-2"
                                        style={{ background: '#0ea5e9', border: 'none' }}
                                    >
                                        <i className="ri-refresh-line"></i>
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Tableau des encours */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="bg-transparent border-0 pt-4 pb-3">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="section-icon-small">
                                        <i className="ri-group-line text-danger"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Détail des encours</h6>
                                </div>
                                <Badge className="px-3 py-2 rounded-pill" style={{ background: '#ef4444' }}>
                                    {encours.length} clients
                                </Badge>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4">Client</th>
                                            <th className="text-center">Factures</th>
                                            <th className="text-end">Encours total</th>
                                            <th>Dernière facture</th>
                                            <th>Échéance</th>
                                            <th className="text-center">Retard</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.map((client, index) => {
                                            const joursRetard = client.jours_retard_max || 0;
                                            return (
                                                <tr key={client.id_client} className="client-row">
                                                    <td className="ps-4">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="avatar-icon">
                                                                <i className="ri-user-3-line"></i>
                                                            </div>
                                                            <div>
                                                                <Link to={`/ventes/clients/${client.id_client}`} className="table-link">
                                                                    {client.raison_sociale}
                                                                </Link>
                                                                <div>
                                                                    <small className="text-muted">{client.code_client}</small>
                                                                    {client.telephone && (
                                                                        <small className="text-muted ms-2">
                                                                            <i className="ri-phone-line me-1"></i>
                                                                            {client.telephone}
                                                                        </small>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <Badge className="rounded-pill px-3" style={{ background: '#e2f3f5', color: '#fff' }}>
                                                            {client.nb_factures}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-end fw-bold text-danger">
                                                        {formatCurrency(client.total_encours)}
                                                    </td>
                                                    <td>
                                                        <span className="text-muted">
                                                            <i className="ri-calendar-line me-1"></i>
                                                            {formatDate(client.derniere_facture)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="text-muted">
                                                            <i className="ri-calendar-check-line me-1"></i>
                                                            {formatDate(client.derniere_echeance)}
                                                        </span>
                                                    </td>
                                                    <td className="text-center">
                                                        {getRetourBadge(joursRetard)}
                                                    </td>
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Voir le détail</Tooltip>}>
                                                                <Button 
                                                                    variant="outline-info" 
                                                                    size="sm"
                                                                    className="action-btn"
                                                                    onClick={() => {
                                                                        setClientDetail(client);
                                                                        fetchDetailClient(client.id_client);
                                                                    }}
                                                                >
                                                                    <i className="ri-eye-line"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Saisir un règlement</Tooltip>}>
                                                                <Button 
                                                                    variant="outline-success" 
                                                                    size="sm"
                                                                    className="action-btn"
                                                                    onClick={() => navigate(`/ventes/NouveauReglement?client=${client.id_client}`)}
                                                                >
                                                                    <i className="ri-bank-card-line"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {currentItems.length === 0 && (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5">
                                                    <i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i>
                                                    <h5>Aucun client trouvé</h5>
                                                    <p className="text-muted">Aucun encours client pour cette période</p>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                        
                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                <div className="text-muted small">
                                    Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, encours.length)} sur {encours.length} clients
                                </div>
                                <Pagination className="mb-0">
                                    <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                    {renderPagination()}
                                    <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                </Pagination>
                            </div>
                        )}
                    </Card>

                    {/* Modal de détail */}
                    <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered className="modern-modal">
                        <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#0ea5e9' }}>
                            <Modal.Title className="w-100">
                                <div className="text-center">
                                    <div className="confirm-icon mb-2">
                                        <i className="ri-file-list-line text-white" style={{ fontSize: '3rem' }}></i>
                                    </div>
                                    <h5 className="fw-bold text-white">Factures impayées</h5>
                                    <p className="text-white-50 small mb-0">{clientDetail?.raison_sociale}</p>
                                </div>
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body className="pt-4">
                            {loadingDetail ? (
                                <div className="text-center py-4">
                                    <Spinner animation="border" variant="primary" style={{ color: '#0ea5e9' }} />
                                </div>
                            ) : facturesClient.length === 0 ? (
                                <p className="text-muted text-center py-4">Aucune facture impayée</p>
                            ) : (
                                <div className="table-responsive">
                                    <Table hover className="align-middle mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th>Facture</th>
                                                <th>Date</th>
                                                <th>Échéance</th>
                                                <th className="text-end">Montant</th>
                                                <th className="text-end">Réglé</th>
                                                <th className="text-end">Solde</th>
                                                <th className="text-center">Retard</th>
                                                <th className="text-center">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {facturesClient.map(f => (
                                                <tr key={f.id_facture}>
                                                    <td>
                                                        <Link to={`/ventes/facturesDetails/${f.id_facture}`} className="table-link">
                                                            {f.numero_facture}
                                                        </Link>
                                                    </td>
                                                    <td>{formatDate(f.date_facture)}</td>
                                                    <td className={f.jours_retard > 0 ? 'text-danger fw-bold' : ''}>
                                                        {formatDate(f.date_echeance)}
                                                    </td>
                                                    <td className="text-end">{formatCurrency(f.montant_ttc)}</td>
                                                    <td className="text-end text-success">{formatCurrency(f.montant_regle)}</td>
                                                    <td className="text-end fw-bold text-danger">{formatCurrency(f.solde)}</td>
                                                    <td className="text-center">
                                                        {f.jours_retard > 0 ? (
                                                            <span className="status-badge" style={{ background: '#ef444415', color: '#ef4444' }}>
                                                                {f.jours_retard}j
                                                            </span>
                                                        ) : (
                                                            <span className="status-badge" style={{ background: '#10b98115', color: '#10b981' }}>
                                                                OK
                                                            </span>
                                                        )}
                                               </td>
                                                    <td className="text-center">
                                                        <Button 
                                                            variant="outline-success" 
                                                            size="sm"
                                                            className="action-btn"
                                                            onClick={() => navigate(`/ventes/NouveauReglement?facture=${f.id_facture}&client=${clientDetail.id_client}`)}
                                                        >
                                                            <i className="ri-bank-card-line"></i>
                                                        </Button>
                                                </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                            <Button variant="outline-secondary" onClick={() => setShowDetailModal(false)} className="rounded-pill px-4">
                                Fermer
                            </Button>
                            <Button 
                                variant="primary" 
                                onClick={() => navigate(`/ventes/NouveauReglement?client=${clientDetail?.id_client}`)}
                                className="rounded-pill px-4"
                                style={{ background: '#0ea5e9', border: 'none' }}
                            >
                                <i className="ri-bank-card-line me-2"></i>
                                Saisir un règlement
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </div>
                <Footer />
            </div>

            <style>{`
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-danger { border-left-color: #ef4444; }
                .kpi-warning { border-left-color: #f59e0b; }
                .kpi-info { border-left-color: #0ea5e9; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-danger .kpi-icon { background: #fef2f2; color: #ef4444; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-info .kpi-icon { background: #e2f3f5; color: #0ea5e9; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.8rem;
                    border-radius: 30px;
                    font-size: 0.7rem;
                    font-weight: 500;
                }
                
                /* Stat Card Mini */
                .stat-card-mini {
                    background: #f8fafc;
                    padding: 1rem;
                    border-radius: 12px;
                    text-align: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                }
                .stat-card-mini:hover {
                    transform: translateY(-2px);
                    border-color: #0ea5e9;
                }
                
                /* Avatar Icon */
                .avatar-icon {
                    width: 40px;
                    height: 40px;
                    background: #f1f5f9;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #ef4444;
                }
                .avatar-icon i {
                    font-size: 1.2rem;
                }
                
                /* Section Icon Small */
                .section-icon-small {
                    width: 32px;
                    height: 32px;
                    background: #f1f5f9;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                /* Table Link */
                .table-link {
                    color: #0ea5e9;
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                /* Action Buttons */
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-info:hover {
                    background: #0ea5e9;
                    border-color: #0ea5e9;
                    color: white;
                }
                .btn-outline-success:hover {
                    background: #10b981;
                    border-color: #10b981;
                    color: white;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .client-row {
                    transition: all 0.2s ease;
                }
                
                .client-row:hover {
                    background: #f8fafc;
                }
                
                /* Pagination */
                .pagination {
                    gap: 5px;
                }
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                .pagination .page-item.active .page-link {
                    background: #0ea5e9;
                    border-color: #0ea5e9;
                    color: white;
                    box-shadow: 0 4px 10px rgba(14, 165, 233, 0.3);
                }
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
                
                /* Modern Modal */
                .modern-modal .modal-content {
                    border-radius: 20px;
                    border: none;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                    overflow: hidden;
                }
                .confirm-icon {
                    animation: scaleIn 0.5s ease-out;
                }
                @keyframes scaleIn {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); opacity: 1; }
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
            `}</style>
        </>
    );
}